import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db, storage } from "../../services/firebaseConnection";
import { useAuth } from "../../context/AuthContext";
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

import up from "../../assets/icons/adm/up.png";
import turn from "../../assets/icons/box-main/icon-home.png";
import "./adm.css";

const Adm = () => {
  const { usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const inputArquivoRef = useRef(null);

  const [employeeData, setEmployeeData] = useState({
    nome: "FUNCIONARIO",
    funcao: "FUNÇÃO",
    obra: "OBRA",
    cpf: "CPF",
    situacao: "",
    matrizesAplicadas: [],
  });

  const [trainingData, setTrainingData] = useState({
    tipo: "TREINAMENTO",
    avaliador: "",
    novaData: "",
    usuarioLogado: "",
  });

  const [arquivoAnexo, setArquivoAnexo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [originalTrainingDocId, setOriginalTrainingDocId] = useState("");
  const [originalTrainingData, setOriginalTrainingData] = useState(null);

  useEffect(() => {
    if (usuarioLogado?.nome) {
      setTrainingData((prev) => ({
        ...prev,
        usuarioLogado: usuarioLogado.nome,
      }));
    }
  }, [usuarioLogado]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const employeeId = searchParams.get("employeeId");
    const trainingType = searchParams.get("trainingType");

    if (employeeId && trainingType) {
      fetchEmployeeAndTrainingData(
        employeeId,
        decodeURIComponent(trainingType),
      );
    } else {
      setLoading(false);
    }
  }, [location]);

  const normalizarTexto = (valor = "") => {
    return String(valor || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .replace(/\s+/g, " ")
      .toUpperCase();
  };

  const fetchEmployeeAndTrainingData = async (employeeId, trainingType) => {
    try {
      setLoading(true);

      const employeeDocRef = doc(db, "funcionarios", employeeId);
      const employeeDocSnap = await getDoc(employeeDocRef);

      if (employeeDocSnap.exists()) {
        const employeeDataFromFirebase = employeeDocSnap.data();

        setEmployeeData({
          nome: employeeDataFromFirebase.nome || "FUNCIONARIO",
          funcao: employeeDataFromFirebase.funcao || "FUNÇÃO",
          obra: employeeDataFromFirebase.obra || "OBRA",
          cpf: employeeDataFromFirebase.cpf || employeeId,
          situacao: employeeDataFromFirebase.situacao || "",
          matrizesAplicadas: employeeDataFromFirebase.matrizesAplicadas || [],
        });
      }

      const trainingsRef = collection(
        db,
        "funcionarios",
        employeeId,
        "treinamentos",
      );

      const q = query(
        trainingsRef,
        where("treinamentoNome", "==", trainingType),
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const treinamentosEncontrados = querySnapshot.docs
          .map((trainingDoc) => ({
            id: trainingDoc.id,
            ...trainingDoc.data(),
          }))
          .filter((treinamento) => treinamento.substituido !== true)
          .sort((a, b) => {
            const dataA = new Date(a.data || a.dataCadastro || 0).getTime();
            const dataB = new Date(b.data || b.dataCadastro || 0).getTime();
            return dataB - dataA;
          });

        const treinamentoAtual = treinamentosEncontrados[0] || {
          id: querySnapshot.docs[0].id,
          ...querySnapshot.docs[0].data(),
        };

        setOriginalTrainingDocId(treinamentoAtual.id);
        setOriginalTrainingData(treinamentoAtual);

        setTrainingData((prev) => ({
          ...prev,
          tipo:
            treinamentoAtual.treinamentoNome ||
            treinamentoAtual.tipo ||
            "TREINAMENTO",
          avaliador: treinamentoAtual.avaliador || "",
          novaData: "",
        }));
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (cpf) => {
    if (!cpf || cpf === "CPF") return "CPF";

    const numbers = cpf.replace(/\D/g, "");

    if (numbers.length !== 11) return "CPF";

    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const cleanCPF = (cpf) => cpf?.replace(/\D/g, "") || "";

  const limparNomeArquivo = (valor = "") => {
    return valor
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w.-]/g, "_")
      .toUpperCase();
  };

  const validarArquivoPDF = (arquivo) => {
    if (!arquivo) return false;

    return (
      arquivo.type === "application/pdf" ||
      arquivo.name.toLowerCase().endsWith(".pdf")
    );
  };

  const handleArquivoChange = (e) => {
    const arquivo = e.target.files?.[0];

    if (!arquivo) {
      setArquivoAnexo(null);
      return;
    }

    if (!validarArquivoPDF(arquivo)) {
      alert("Só é permitido anexar arquivos em PDF.");

      setArquivoAnexo(null);

      if (inputArquivoRef.current) {
        inputArquivoRef.current.value = "";
      }

      return;
    }

    setArquivoAnexo(arquivo);
  };

  const removerArquivo = () => {
    setArquivoAnexo(null);

    if (inputArquivoRef.current) {
      inputArquivoRef.current.value = "";
    }
  };

  const validarDataTreinamento = (data) => {
    if (!data) return false;

    const partes = data.split("-");
    if (partes.length !== 3) return false;

    const [ano, mes, dia] = partes;

    if (!/^\d{4}$/.test(ano)) return false;
    if (ano.startsWith("0")) return false;
    if (!/^\d{2}$/.test(mes)) return false;
    if (!/^\d{2}$/.test(dia)) return false;

    const anoNumero = Number(ano);
    const mesNumero = Number(mes);
    const diaNumero = Number(dia);

    if (anoNumero < 1900 || anoNumero > 2100) return false;
    if (mesNumero < 1 || mesNumero > 12) return false;
    if (diaNumero < 1 || diaNumero > 31) return false;

    const dataObj = new Date(anoNumero, mesNumero - 1, diaNumero);

    return (
      dataObj.getFullYear() === anoNumero &&
      dataObj.getMonth() === mesNumero - 1 &&
      dataObj.getDate() === diaNumero
    );
  };

  const criarAuditoria = async ({ acao, entidade, entidadeId, detalhes }) => {
    try {
      await addDoc(collection(db, "auditoria_sistema"), {
        acao,
        entidade,
        entidadeId,
        responsavelNome: usuarioLogado?.nome || "NÃO IDENTIFICADO",
        responsavelId:
          usuarioLogado?.id || usuarioLogado?.cpf || usuarioLogado?.email || "",
        dataHora: new Date().toISOString(),
        detalhes,
      });
    } catch (error) {
      console.error("Erro ao criar auditoria:", error);
    }
  };

  const handleUpdateTraining = async () => {
    if (!trainingData.novaData) {
      alert("Por favor, informe a nova data!");
      return;
    }

    if (!validarDataTreinamento(trainingData.novaData)) {
      alert("A data informada é inválida.");
      return;
    }

    if (!trainingData.avaliador.trim()) {
      alert("Informe o avaliador/instrutor do treinamento.");
      return;
    }

    if (!arquivoAnexo) {
      alert("Anexe o novo PDF do treinamento antes de atualizar.");
      return;
    }

    if (!validarArquivoPDF(arquivoAnexo)) {
      alert("Só é permitido atualizar com arquivo PDF.");
      return;
    }

    if (!originalTrainingDocId || !originalTrainingData) {
      alert("Erro: treinamento original não encontrado!");
      return;
    }

    try {
      setAtualizando(true);

      const cpfNumbers = cleanCPF(employeeData.cpf);
      const nomeTreinamento = normalizarTexto(trainingData.tipo);
      const nomeTreinamentoLimpo = limparNomeArquivo(nomeTreinamento);
      const nomeArquivoLimpo = limparNomeArquivo(arquivoAnexo.name);
      const dataArquivo = Date.now();
      const agora = new Date().toISOString();

      const obraAtual = normalizarTexto(employeeData.obra);
      const funcaoAtual = normalizarTexto(employeeData.funcao);
      const obraLimpa = limparNomeArquivo(obraAtual);
      const funcaoLimpa = limparNomeArquivo(funcaoAtual);

      const caminhoArquivo = `treinamentos/${cpfNumbers}/${obraLimpa}/${funcaoLimpa}/${nomeTreinamentoLimpo}/${dataArquivo}_${nomeArquivoLimpo}`;

      const arquivoRef = ref(storage, caminhoArquivo);

      await uploadBytes(arquivoRef, arquivoAnexo);

      const arquivoUrl = await getDownloadURL(arquivoRef);

      const trainingDocRef = doc(
        db,
        "funcionarios",
        cpfNumbers,
        "treinamentos",
        originalTrainingDocId,
      );

      const novoTreinamento = {
        tipo: originalTrainingData.tipo || "",
        treinamentoNome:
          originalTrainingData.treinamentoNome || nomeTreinamento || "",

        data: trainingData.novaData,
        avaliador: normalizarTexto(trainingData.avaliador),

        colaboradorNome: normalizarTexto(employeeData.nome),
        colaboradorCpf: cpfNumbers,
        colaboradorId: cpfNumbers,

        obraNaData: obraAtual,
        funcaoNaData: funcaoAtual,
        matrizesNaData:
          originalTrainingData.matrizesNaData ||
          employeeData.matrizesAplicadas ||
          [],

        cadastradoPor: normalizarTexto(trainingData.usuarioLogado),
        cadastradoPorId:
          usuarioLogado?.id || usuarioLogado?.cpf || usuarioLogado?.email || "",

        validadeMeses: originalTrainingData.validadeMeses || "",
        possuiVencimento:
          originalTrainingData.possuiVencimento !== undefined
            ? Boolean(originalTrainingData.possuiVencimento)
            : true,

        anexoNome: arquivoAnexo.name,
        anexoUrl: arquivoUrl,
        anexoPath: caminhoArquivo,
        anexoTipo: "application/pdf",
        anexoTamanho: arquivoAnexo.size || 0,

        tipoLancamento: "ATUALIZACAO",
        treinamentoAnteriorId: originalTrainingDocId,
        treinamentoAnteriorData: originalTrainingData.data || "",
        treinamentoAnteriorObra: originalTrainingData.obraNaData || "",
        treinamentoAnteriorFuncao: originalTrainingData.funcaoNaData || "",

        substituido: false,
        statusHistorico: "ATUAL",

        dataCadastro: agora,
        dataAtualizacao: agora,
        atualizadoPor: normalizarTexto(trainingData.usuarioLogado),
        atualizadoPorId:
          usuarioLogado?.id || usuarioLogado?.cpf || usuarioLogado?.email || "",
      };

      const novoDocRef = await addDoc(
        collection(db, "funcionarios", cpfNumbers, "treinamentos"),
        novoTreinamento,
      );

      await updateDoc(trainingDocRef, {
        substituido: true,
        substituidoPorId: novoDocRef.id,
        substituidoPorTreinamento: novoTreinamento.treinamentoNome,
        substituidoPorData: trainingData.novaData,
        statusHistorico: "SUBSTITUIDO",
        dataSubstituicao: agora,
        atualizadoPor: normalizarTexto(trainingData.usuarioLogado),
        atualizadoPorId:
          usuarioLogado?.id || usuarioLogado?.cpf || usuarioLogado?.email || "",
      });

      await criarAuditoria({
        acao: "ATUALIZACAO_TREINAMENTO",
        entidade: "TREINAMENTO",
        entidadeId: novoDocRef.id,
        detalhes: {
          colaboradorNome: employeeData.nome,
          colaboradorCpf: cpfNumbers,
          treinamento: novoTreinamento.treinamentoNome,
          treinamentoAnteriorId: originalTrainingDocId,
          treinamentoNovoId: novoDocRef.id,
          obraAnterior:
            originalTrainingData.obraNaData ||
            employeeData.obra ||
            "NÃO INFORMADA",
          funcaoAnterior:
            originalTrainingData.funcaoNaData ||
            employeeData.funcao ||
            "NÃO INFORMADA",
          obraAtual,
          funcaoAtual,
          dataAnterior: originalTrainingData.data || "",
          novaData: trainingData.novaData,
          novoAvaliador: normalizarTexto(trainingData.avaliador),
          anexoPath: caminhoArquivo,
          regra: "REGISTRO_ANTIGO_MANTIDO_E_NOVO_REGISTRO_CRIADO",
        },
      });

      alert("Treinamento atualizado com sucesso!");
      navigate("/pendencia");
    } catch (error) {
      console.error("Erro ao atualizar treinamento:", error);
      alert("Erro ao atualizar treinamento!");
    } finally {
      setAtualizando(false);
    }
  };

  const paginaPendencia = () => {
    navigate("/pendencia");
  };

  if (loading) {
    return (
      <div className="container-adm">
        <div className="container-adm-all">
          <div className="loading-adm">Carregando dados...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-adm">
      <div className="container-adm-all">
        <div className="dados-adm-geral">
          <div className="dados-primarios-adm">
            <p>{employeeData.nome}</p>
            <p>{employeeData.obra}</p>
          </div>

          <div className="dados-secundarios-adm">
            <p>{employeeData.funcao}</p>
            <p>{formatCPF(employeeData.cpf)}</p>
          </div>
        </div>

        <div className="dados-adm">
          <p>{trainingData.tipo}</p>

          <input
            type="text"
            placeholder="AVALIADOR"
            value={trainingData.avaliador.toUpperCase()}
            onChange={(e) =>
              setTrainingData({
                ...trainingData,
                avaliador: e.target.value.toLocaleUpperCase(),
              })
            }
            className="input-adm"
          />

          <input
            type="date"
            value={trainingData.novaData}
            onChange={(e) =>
              setTrainingData({
                ...trainingData,
                novaData: e.target.value,
              })
            }
            className="input-data-adm"
          />
        </div>

        <div className="campo-anexo-adm">
          <span className="label-anexo-adm">NOVO ANEXO DO TREINAMENTO</span>

          <input
            ref={inputArquivoRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleArquivoChange}
            className="input-arquivo-adm"
          />

          {arquivoAnexo && (
            <div className="arquivo-selecionado-adm">
              <p>{arquivoAnexo.name}</p>

              <button type="button" onClick={removerArquivo}>
                REMOVER
              </button>
            </div>
          )}
        </div>

        <div className="campo-usuario-logado-adm">
          <span className="label-usuario-logado-adm">USUÁRIO LOGADO</span>

          <input
            type="text"
            value={trainingData.usuarioLogado}
            readOnly
            className="input-usuario-logado-adm"
          />
        </div>

        <div className="area-botao-atualizar-adm">
          <button
            type="button"
            className="dados-adm-I"
            onClick={handleUpdateTraining}
            disabled={atualizando}
          >
            <span>{atualizando ? "ATUALIZANDO..." : "ATUALIZAR"}</span>
            <img src={up} alt="Atualizar treinamento" />
          </button>
        </div>
      </div>

      <div className="footer-adm">
        <img
          onClick={paginaPendencia}
          className="icon-home-adm"
          src={turn}
          alt="Voltar para pendências"
        />
      </div>
    </div>
  );
};

export default Adm;
